import { initializeApp } from 'firebase/app';
import { GoogleAuthProvider, connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';

export const app = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
});

/*
 * getAuth, which in a browser brings the popup's helpers up from the start.
 *
 * They were held back to the moment somebody signed in, to stop an unreadable
 * "Script error." from inside them reaching the error log from Ethan's phone.
 * That broke signing in from the installed app on an iPhone: Google's page
 * hands its answer back through the helpers' frame, which on iOS has to be up
 * and listening before the sign-in window opens -- started from the tap, it
 * never heard the answer, and the button spun for good. Nobody signed in for
 * six days and it was only noticed when Aana reinstalled. The odd script error
 * is the price; signing in is not optional.
 */
export const auth = getAuth(app);

export const googleProvider = new GoogleAuthProvider();
// The allowlist is by email, so make the account chooser explicit rather than
// silently reusing whatever Google session the browser already has.
googleProvider.setCustomParameters({ prompt: 'select_account' });

/**
 * Offline persistence is what lets a text message queue up on bad signal and
 * send itself later, which is most of the "offline" story in the spec.
 */
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

export const functions = getFunctions(app);

export const MEDIA_GATEWAY = import.meta.env.VITE_MEDIA_GATEWAY;

/**
 * Local development against the emulator suite.
 *
 * The point is signing in without Google: the Auth emulator serves its own
 * account picker, so the Google provider flow completes against a made-up
 * address and never leaves the machine. Useful in the iOS Simulator, where
 * a real Google login is slow and pollutes a real account with test data.
 *
 * Media will not load in this mode. The gateway verifies real Firebase ID
 * tokens against Google's public keys, and an emulator token is signed with a
 * test key it will rightly reject.
 */
export const USING_EMULATORS = import.meta.env.VITE_USE_EMULATORS === '1';

if (USING_EMULATORS) {
  // Must happen before any read or write, which is why it lives here rather
  // than in a component. Individually guarded because connectFirestoreEmulator
  // throws if the persistent cache has already started, and a throw at module
  // scope takes the whole app down before React mounts — a blank screen with
  // no explanation.
  const host = window.location.hostname || '127.0.0.1';

  try {
    connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  } catch (err) {
    console.warn('auth emulator not connected', err);
  }
  try {
    connectFirestoreEmulator(db, host, 8088);
  } catch (err) {
    console.warn('firestore emulator not connected', err);
  }
  try {
    connectFunctionsEmulator(functions, host, 5001);
  } catch (err) {
    console.warn('functions emulator not connected', err);
  }
}
